import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { addCartItem } from '../../firebase/Cart';
import ProductDescriptionBlock from '../ProductDescription/ProductDescriptionBlock';
import ProductAttributeFields from '../ProductAttributeFields/ProductAttributeFields';
import { useProductAttributeForm } from '../../hooks/useProductAttributeForm';
import placeholderShirt from '../../assets/images/placeholder-shirt.png';

function buildImageArray(pd) {
    if (Array.isArray(pd.images) && pd.images.length > 0) {
        const filtered = pd.images.filter(Boolean);
        if (filtered.length > 0) return filtered;
    }
    if (pd.imageUrl || pd.image) return [pd.imageUrl || pd.image];
    return [placeholderShirt];
}

export default function DynamicProductDetailsView({ productData, isPreview, currentUser, onShowAuth }) {
    const navigate = useNavigate();
    const [mainImage, setMainImage] = useState(0);
    const [quantity, setQuantity] = useState(0);
    const [validationError, setValidationError] = useState('');
    const [addingToCart, setAddingToCart] = useState(false);
    const [maxOrderQty, setMaxOrderQty] = useState(50);

    const {
        definitions,
        selections,
        setSelection,
        loading: attributesLoading,
        fetchError: attributesError,
        attributesPriceTotal,
        validateAttributes,
        cartSelections,
    } = useProductAttributeForm(productData);

    useEffect(() => {
        getDoc(doc(db, 'settings', 'store')).then((snap) => {
            if (!snap.exists()) return;
            const data = snap.data() || {};
            const limit = Number(data.deliveryConfig?.maxItemsPerOrder || data.orderLimits?.maxItemsPerOrder || 50);
            if (limit > 0) setMaxOrderQty(limit);
        }).catch(() => {});
    }, []);

    const displayImages = buildImageArray(productData);
    const safeMainImage = Math.min(mainImage, Math.max(displayImages.length - 1, 0));
    const goPrev = () => setMainImage((i) => (i - 1 + displayImages.length) % displayImages.length);
    const goNext = () => setMainImage((i) => (i + 1) % displayImages.length);

    const unitPrice = (productData.price || 0) + attributesPriceTotal;
    const finalPrice = quantity * unitPrice;

    const validate = () => {
        const attrErr = validateAttributes();
        if (attrErr) return attrErr;
        if (quantity === 0) return 'Please enter a quantity.';
        if (quantity > maxOrderQty) return `Maximum order quantity is ${maxOrderQty} items.`;
        return null;
    };

    const handleAddToCart = async () => {
        if (isPreview) return;
        setValidationError('');
        const err = validate();
        if (err) { setValidationError(err); return; }
        if (!currentUser) { onShowAuth(); return; }
        setAddingToCart(true);
        try {
            const productImages = buildImageArray(productData);
            const cartItem = {
                name: productData.name || 'Product',
                image: productImages[0] || placeholderShirt,
                imageUrl: productData.imageUrl || productData.image || productImages[0] || '',
                type: productData.name,
                category: productData.category || '',
                subcategory: productData.subcategory || '',
                quantity,
                price: unitPrice,
                ...(cartSelections && { selectedAttributes: cartSelections }),
            };
            const { error: cartErr } = await addCartItem(currentUser.uid, cartItem);
            if (cartErr) { setValidationError('Failed to add item to cart. Please try again.'); return; }
            navigate('/cart');
        } catch (e) {
            console.error(e);
            setValidationError('An unexpected error occurred.');
        } finally {
            setAddingToCart(false);
        }
    };

    const isOverOrderLimit = quantity > maxOrderQty;
    const displayError = validationError || attributesError;

    return (
        <div className="product-details-container">
            <div className="page-header">
                <h1>{productData.name || 'Product'}</h1>
                <p>Select your options and quantity to order.</p>
            </div>
            <div className="product-content">
                <div className="image-gallery">
                    <div className="main-image-wrapper">
                        {displayImages.length > 1 && (
                            <button type="button" className="main-image-arrow main-image-arrow--left" onClick={goPrev}>&#8249;</button>
                        )}
                        <div className="main-image">
                            <img src={displayImages[safeMainImage] || placeholderShirt} alt={productData.name} />
                        </div>
                        {displayImages.length > 1 && (
                            <button type="button" className="main-image-arrow main-image-arrow--right" onClick={goNext}>&#8250;</button>
                        )}
                        {displayImages.length > 1 && (
                            <div className="main-image-dots">
                                {displayImages.map((_, i) => (
                                    <button key={i} type="button" className={`main-image-dot ${safeMainImage === i ? 'active' : ''}`} onClick={() => setMainImage(i)} />
                                ))}
                            </div>
                        )}
                    </div>
                    {displayImages.length > 1 && (
                        <div className="thumbnails">
                            {displayImages.map((img, i) => (
                                <div key={i} className={`thumbnail ${safeMainImage === i ? 'active' : ''}`} onClick={() => setMainImage(i)}>
                                    <img src={img} alt={`View ${i + 1}`} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="product-config">
                    {attributesLoading && (
                        <div className="config-group">
                            <p className="pd-no-options">Loading options...</p>
                        </div>
                    )}

                    {!attributesLoading && (
                        <ProductAttributeFields
                            definitions={definitions}
                            selections={selections}
                            onChange={setSelection}
                            disabled={isPreview}
                            prefix="pd"
                            fieldClassName="config-group"
                            labelClassName={null}
                        />
                    )}

                    <div className="config-group">
                        <label>Quantity <span className="info-icon">ⓘ<span className="info-tooltip">Enter the quantity you need</span></span></label>
                        <div className="pd-order-limit-bar">
                            <span className={`pd-order-limit-text ${isOverOrderLimit ? 'pd-order-limit-text--over' : quantity > maxOrderQty * 0.8 ? 'pd-order-limit-text--warn' : ''}`}>
                                {quantity} / {maxOrderQty} items
                            </span>
                            {isOverOrderLimit && <span className="pd-order-limit-badge">Over limit</span>}
                        </div>
                        <div className="quantity-stepper">
                            <button type="button" className="quantity-stepper-btn" disabled={isPreview || quantity <= 0}
                                onClick={() => setQuantity((q) => Math.max(0, q - 1))}>−</button>
                            <input type="number" min="0" max={maxOrderQty}
                                value={quantity === 0 ? '' : quantity}
                                placeholder="0" disabled={isPreview}
                                onChange={(e) => {
                                    if (isPreview) return;
                                    const v = e.target.value;
                                    if (v === '') { setQuantity(0); return; }
                                    const n = parseInt(v, 10);
                                    if (!isNaN(n) && n >= 0) setQuantity(Math.min(n, maxOrderQty));
                                }}
                                className={`quantity-input${isOverOrderLimit ? ' quantity-input--over' : ''}`}
                            />
                            <button type="button" className="quantity-stepper-btn" disabled={isPreview || quantity >= maxOrderQty}
                                onClick={() => setQuantity((q) => q + 1)}>+</button>
                        </div>
                    </div>

                    <div className="total-section">
                        <span className="total-label">Total:</span>
                        <span className="total-amount">₱ {finalPrice.toLocaleString()}</span>
                    </div>

                    {!isPreview && isOverOrderLimit && <div className="pd-order-limit-error">⚠ Maximum order is {maxOrderQty} items.</div>}
                    {!isPreview && displayError && <div className="error-message">{displayError}</div>}

                    <button type="button" className="add-to-cart-btn" onClick={handleAddToCart}
                        disabled={addingToCart || isPreview || isOverOrderLimit || attributesLoading}
                        style={isPreview ? { backgroundColor: '#aaa', cursor: 'not-allowed' } : {}}>
                        {isPreview ? 'Not Available in Preview Mode' : addingToCart ? 'Adding…' : 'Add to Cart'}
                    </button>
                </div>
            </div>

            {productData.description && (
                <ProductDescriptionBlock
                    description={productData.description}
                    title={`About ${productData.name || 'This Product'}`}
                    highlightColor="#111"
                />
            )}
        </div>
    );
}
